import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CustomLink } from 'components/CustomLink';
import useApiContext from 'hooks/useApiContext';
import routes from 'router/routes';
import { A, T } from 'TOMA';
import s from './Header.module.scss';
import { eRoles } from 'services/User/User.repository';
import { useTranslation } from 'react-i18next';
import useRepo from '../../hooks/useRepo';
import { iCompany } from '../../services/Company/Company.repository';
import { use } from 'i18next';
import path from 'path';
import {
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import { useHeaderSlot } from '../../contexts/HeaderSlotContext';
import LogoutIcon from '@mui/icons-material/Logout';
import LanguageIcon from '@mui/icons-material/Language';

function Header(): JSX.Element {
  const navigate = useNavigate();
  const {
    userRepository,
    companyRepository,
    eventRepository,
    exhibitorEventRepository,
  } = useApiContext();
  const [company, setCompany] = useState<iCompany>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const pathSegments = window.location.pathname.split('/');
  const lastSegment = pathSegments[pathSegments.length - 2];
  const dynamicSegment = pathSegments[1];
  const { response, loading } = useRepo(
    // eslint-disable-next-line
    async () => await companyRepository.getCompanyRest(dynamicSegment),
    []
  );
  useEffect(() => {
    if (!loading) {
      setCompany(response);
    }
  }, [loading, response]);
  const user = userRepository.getUser();
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const code = queryParams.get('code');
  const handleLanguageChange = (lang: string): void => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('appLanguage', lang);
    window.location.reload();
  };
  const { pathname } = useLocation();
  const formatDate = (dateString: any): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  };
  const [event, setEvent] = React.useState<any>(null);
  const isPreview = pathname.includes('preview');
  const eventId = pathSegments[pathSegments.length - 1];
  const event_id = useParams().eventId;
  const pathEvent = pathSegments[pathSegments.length - 2];
  const [eventDisable, setEventDisable] = useState(true);
  const exhibitorEventId = pathSegments[pathSegments.length - 1];
  useEffect(() => {
    let cancelled = false;
    const applyEvent = (data: any): void => {
      if (cancelled) return;
      const eventData = Array.isArray(data) ? data[0] : data;
      if (!eventData) return;
      setEvent({
        ...eventData,
        logo:
          eventData.company_logo_uuid && process.env.REACT_APP_API_URL
            ? `${process.env.REACT_APP_API_URL}/storage/uploads/640/${eventData.company_logo_uuid}.${eventData.company_logo_extension}`
            : null,
      });
    };
    if (
      (user?.role === eRoles.ADMIN ||
        user?.role === eRoles.MODERATOR ||
        user?.role === eRoles.EXHIBITOR) &&
      lastSegment === 'event-registration'
    ) {
      setEventDisable(false);
      setEvent(null);
      // The URL carries the exhibitor-event id; resolve the selected event
      // from it instead of the company's default (first) event.
      exhibitorEventRepository
        .getExhibitorEvent(Number(exhibitorEventId))
        .then(async (detail) => {
          const selectedEventId =
            detail?.event?.id ?? detail?.exhibitorEvent?.event_id;
          if (!selectedEventId) return;
          return await eventRepository.getEventById(String(selectedEventId));
        })
        .then(applyEvent)
        .catch(() => undefined);
    } else {
      setEventDisable(true);
      eventRepository
        .getEventById(event_id ?? '')
        .then(applyEvent)
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [lastSegment, exhibitorEventId, user?.role]);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(e.currentTarget);
  };
  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const navLinkSx = {
    color: 'rgba(17,24,39,0.85)',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 500,
    px: 1.5,
    py: 0.5,
    borderRadius: '6px',
    transition: 'all 0.15s',
    '&:hover': {
      color: '#111827',
      backgroundColor: 'rgba(17,24,39,0.08)',
    },
  };

  const userInitials = user
    ? `${user.firstname?.[0] ?? ''}${user.lastname?.[0] ?? ''}`.toUpperCase()
    : '';

  const { isSlotClaimed } = useHeaderSlot();

  return (
    <>
      <div id="app-header" className={s.Header}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: '#ffffff',
            zIndex: 9,
            borderBottom: '1px solid rgba(17,24,39,0.08)',
          }}
        >
          <Toolbar
            sx={{
              minHeight: '70px !important',
              px: { xs: 2, md: 3 },
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            {/* Left: Company logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {company?.logo_path ? (
                <Box
                  component="a"
                  href={`${routes.home2.path}events`}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <Box
                    component="img"
                    src={company.logo_path}
                    alt="Logo"
                    sx={{ height: 44, width: 'auto', objectFit: 'contain' }}
                  />
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: '#111827',
                    fontWeight: 700,
                    fontSize: '18px',
                    letterSpacing: '-0.3px',
                  }}
                >
                  Event Manager
                </Typography>
              )}
            </Box>

            {/* Center: Navigation links OR header slot for special pages */}
            <Box
              className={s.Header__desk}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flex: 1,
                justifyContent: isSlotClaimed ? 'flex-start' : 'center',
              }}
            >
              {/* Portal target — editor/other pages inject controls here */}
              {isSlotClaimed && (
                <div
                  id="app-header-center-slot"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    gap: 8,
                  }}
                />
              )}
              {!isSlotClaimed &&
                user?.role &&
                ![eRoles.ADMIN, eRoles.MODERATOR, eRoles.SUPER_ADMIN].includes(
                  user?.role
                ) && (
                  <Box
                    component={CustomLink as any}
                    to={routes.home.path.replace(/\/$/, '')}
                    color="black"
                    textDecoration="none"
                    sx={{
                      ...navLinkSx,
                      ...(pathname === routes.home.path
                        ? {
                            color: '#111827',
                            backgroundColor: 'rgba(17,24,39,0.12)',
                          }
                        : {}),
                    }}
                  >
                    {t('header:societe')}
                  </Box>
                )}
              {!isSlotClaimed &&
                user?.role &&
                [eRoles.SUPER_ADMIN].includes(user?.role) && (
                  <>
                    <Box
                      component={CustomLink as any}
                      to={routes.users.path}
                      color="black"
                      textDecoration="none"
                      sx={{
                        ...navLinkSx,
                        ...(pathname === routes.users.path
                          ? {
                              color: '#111827',
                              backgroundColor: 'rgba(17,24,39,0.12)',
                            }
                          : {}),
                      }}
                    >
                      {t('header:users')}
                    </Box>
                    <Box
                      component={CustomLink as any}
                      to={routes.universes.path}
                      color="black"
                      textDecoration="none"
                      sx={{
                        ...navLinkSx,
                        ...(pathname === routes.universes.path
                          ? {
                              color: '#111827',
                              backgroundColor: 'rgba(17,24,39,0.12)',
                            }
                          : {}),
                      }}
                    >
                      {t('header:universes')}
                    </Box>
                    <Box
                      component={CustomLink as any}
                      to={routes.categories.path}
                      color="black"
                      textDecoration="none"
                      sx={{
                        ...navLinkSx,
                        ...(pathname === routes.categories.path
                          ? {
                              color: '#111827',
                              backgroundColor: 'rgba(17,24,39,0.12)',
                            }
                          : {}),
                      }}
                    >
                      {t('header:categories')}
                    </Box>
                    <Box
                      component={CustomLink as any}
                      to={routes.subcategories.path}
                      color="black"
                      textDecoration="none"
                      sx={{
                        ...navLinkSx,
                        ...(pathname === routes.subcategories.path
                          ? {
                              color: '#111827',
                              backgroundColor: 'rgba(17,24,39,0.12)',
                            }
                          : {}),
                      }}
                    >
                      {t('header:subcategories')}
                    </Box>
                  </>
                )}
              {!isSlotClaimed &&
                user?.role &&
                [eRoles.ADMIN].includes(user?.role) && (
                  <Box
                    component={CustomLink as any}
                    to={routes.profile.path}
                    color="black"
                    textDecoration="none"
                    sx={{
                      ...navLinkSx,
                      ...(pathname.includes('users')
                        ? {
                            color: '#111827',
                            backgroundColor: 'rgba(17,24,39,0.12)',
                          }
                        : {}),
                    }}
                  >
                    {t('header:users')}
                  </Box>
                )}
              {!isSlotClaimed &&
                user?.role &&
                [eRoles.ADMIN, eRoles.MODERATOR].includes(user?.role) && (
                  <Box
                    component={CustomLink as any}
                    to={routes.admEventList.path}
                    color="black"
                    textDecoration="none"
                    sx={{
                      ...navLinkSx,
                      ...(pathname.includes('events')
                        ? {
                            color: '#111827',
                            backgroundColor: 'rgba(17,24,39,0.12)',
                          }
                        : {}),
                    }}
                  >
                    {t('header:events')}
                  </Box>
                )}
              {!isSlotClaimed && user?.role === eRoles.EXHIBITOR && (
                <>
                  <Box
                    component={CustomLink as any}
                    to={routes.eventlist.path}
                    color="black"
                    textDecoration="none"
                    sx={{
                      ...navLinkSx,
                      ...(pathname.includes('eventlist')
                        ? {
                            color: '#111827',
                            backgroundColor: 'rgba(17,24,39,0.12)',
                          }
                        : {}),
                    }}
                  >
                    {t('header:events')}
                  </Box>
                  <Box
                    component={CustomLink as any}
                    to={routes.products.path}
                    color="black"
                    textDecoration="none"
                    sx={{
                      ...navLinkSx,
                      ...(pathname.includes('product')
                        ? {
                            color: '#111827',
                            backgroundColor: 'rgba(17,24,39,0.12)',
                          }
                        : {}),
                    }}
                  >
                    {t('header:products')}
                  </Box>
                </>
              )}
              {!isSlotClaimed &&
                user?.role &&
                [eRoles.ADMIN, eRoles.MODERATOR].includes(user?.role) && (
                  <Box
                    component={CustomLink as any}
                    to={routes.home.path.replace(/\/$/, '')}
                    color="black"
                    textDecoration="none"
                    sx={{
                      ...navLinkSx,
                      ...(pathname.includes('exhibitor')
                        ? {
                            color: '#111827',
                            backgroundColor: 'rgba(17,24,39,0.12)',
                          }
                        : {}),
                    }}
                  >
                    {t('header:exhibitor')}
                  </Box>
                )}
              {!isSlotClaimed &&
                user?.role &&
                [eRoles.ADMIN].includes(user?.role) && (
                  <Box
                    component={CustomLink as any}
                    to={routes.company.path}
                    color="black"
                    textDecoration="none"
                    sx={{
                      ...navLinkSx,
                      ...(pathname === routes.company.path
                        ? {
                            color: '#111827',
                            backgroundColor: 'rgba(17,24,39,0.12)',
                          }
                        : {}),
                    }}
                  >
                    {t('header:company_settings')}
                  </Box>
                )}
              {!isSlotClaimed && !user && !isPreview && (
                <>
                  <Box
                    component={CustomLink as any}
                    to={`${routes.login.path}${code ? `?code=${code}` : ''}`}
                    color="black"
                    textDecoration="none"
                    sx={navLinkSx}
                  >
                    {t('login:signIn')}
                  </Box>
                  <Box
                    component={CustomLink as any}
                    to={`${routes.signup.path}${code ? `?code=${code}` : ''}`}
                    color="black"
                    textDecoration="none"
                    sx={navLinkSx}
                  >
                    {t('login:signUp')}
                  </Box>
                </>
              )}
              {/* End default nav — hidden when slot is claimed */}
            </Box>

            {/* Right: Language + Right logo + User menu */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
              }}
            >
              {company?.right_logo_path && (
                <Box
                  component="img"
                  src={company.right_logo_path}
                  alt=""
                  sx={{
                    height: 36,
                    width: 'auto',
                    objectFit: 'contain',
                    mr: 1,
                  }}
                />
              )}
              {user?.role && (
                <Box
                  component="select"
                  value={language}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleLanguageChange(e.target.value)
                  }
                  sx={{
                    background: 'rgba(17,24,39,0.04)',
                    border: '1px solid rgba(17,24,39,0.16)',
                    color: '#111827',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    outline: 'none',
                    '& option': { background: '#ffffff', color: '#111827' },
                  }}
                >
                  <option value="en">EN</option>
                  <option value="fr">FR</option>
                  <option value="ja">JA</option>
                </Box>
              )}
              {!user && (
                <Box
                  component="select"
                  value={language}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleLanguageChange(e.target.value)
                  }
                  sx={{
                    background: 'rgba(17,24,39,0.04)',
                    border: '1px solid rgba(17,24,39,0.16)',
                    color: '#111827',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                >
                  <option value="en">EN</option>
                  <option value="fr">FR</option>
                  <option value="ja">JA</option>
                </Box>
              )}
              {user && (
                <>
                  <IconButton
                    onClick={handleMenuOpen}
                    size="small"
                    sx={{ ml: 0.5 }}
                  >
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        bgcolor: '#333',
                        fontSize: '13px',
                        fontWeight: 600,
                        border: '2px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      {userInitials || '?'}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{
                      elevation: 3,
                      sx: {
                        mt: 0.5,
                        minWidth: 180,
                        borderRadius: '8px',
                        overflow: 'visible',
                        border: '1px solid #eee',
                      },
                    }}
                  >
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: '#111',
                        }}
                      >
                        {user.firstname} {user.lastname}
                      </Typography>
                      <Typography
                        sx={{ fontSize: '12px', color: '#888', mt: 0.25 }}
                      >
                        {user.email}
                      </Typography>
                    </Box>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        handleMenuClose();
                        userRepository.logout().finally(() => {
                          navigate(routes.login.to(), { replace: true });
                        });
                      }}
                      sx={{ fontSize: '14px', gap: 1.5, py: 1.25 }}
                    >
                      <LogoutIcon sx={{ fontSize: 18, color: '#666' }} />
                      {t('header:sign_out')}
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      </div>

      {/* Colored event banner (unchanged logic) */}
      {!isPreview && !eventDisable && (
        <div style={{ backgroundColor: event?.color || undefined }}>
          <T.Block>
            <T.Container>
              <T.Flex halign="space-between" py="40" gap={4}>
                {!eventDisable ? (
                  <T.Col>
                    <>
                      {event?.date_of_the_end && event?.date ? (
                        <>
                          <T.Block my="10">
                            <A.Image
                              className="hidden"
                              src={company?.main_logo_path ?? ''}
                              height="40"
                              width="auto"
                            />
                          </T.Block>
                          <A.Text color="white" mb="10" fontSize="24" fw="bold">
                            {event?.name} {event.year ? event.year : ''}
                          </A.Text>
                          <A.Text color="white" mb="10" fontSize="18">
                            {event?.subtitle}
                          </A.Text>
                          <div className={s.Header__line}></div>
                          <T.Block mt="15">
                            <A.Text color="white" fontSize="18">
                              <T.Flex className="flex" gap={1}>
                                <div>
                                  <A.Icon
                                    icon="calendar2-week-fill"
                                    color="white"
                                  />
                                </div>
                                <div>
                                  Du {formatDate(event?.date)} au{' '}
                                  {formatDate(event?.date_of_the_end)}
                                </div>
                                <div>
                                  <A.Icon icon="geo-fill" color="white" />
                                </div>
                                <div>
                                  {event?.city}, {event?.country}
                                </div>
                              </T.Flex>
                            </A.Text>
                          </T.Block>
                        </>
                      ) : (
                        ''
                      )}
                    </>
                  </T.Col>
                ) : (
                  <>
                    {(pathEvent === event_id || pathEvent === 'events') &&
                      event && (
                        <>
                          <A.Text color="white" mb="10" fontSize="18" fw="bold">
                            {event?.name} {event?.year}
                          </A.Text>
                          <T.Block>
                            <A.Text color="white" fontSize="18">
                              <T.Flex className="flex" gap={1}>
                                <div>{formatDate(event?.date)}</div>
                              </T.Flex>
                            </A.Text>
                          </T.Block>
                        </>
                      )}
                  </>
                )}
              </T.Flex>
            </T.Container>
          </T.Block>
        </div>
      )}
    </>
  );
}

export default Header;
